import React from 'react';
import { useTranslation } from 'react-i18next';

import { IFeedback } from 'api/feedbacks';
import translations from 'services/translations/translations';

// The statuses in the order of the handling, each with its colour (Feedback.css)
const STATUS_ROWS: { key: string; status: null | string }[] = [
  { key: 'new', status: null },
  { key: 'in-progress', status: 'in-progress' },
  { key: 'done', status: 'done' },
  { key: 'not-done', status: 'not-done' },
];

/**
 * How the development has reacted to the feedback listed: the share answered, the feedback
 * by its status as a bar of its own, and the givers told of the response and those who
 * have seen it.
 */
const FeedbackHandling = ({
  feedbacks,
}: {
  feedbacks: IFeedback[];
}): JSX.Element | null => {
  const { t } = useTranslation([translations.common]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.common}:feedback.${key}`, options);

  const total = feedbacks.length;
  if (total === 0) {
    return null;
  }

  // Reacted to: a response written or a status given (e.g. marked done without a text)
  const answered = feedbacks.filter(
    (feedback) => feedback.response || feedback.status
  ).length;
  const notified = feedbacks.filter((feedback) => feedback.notifiedAt).length;
  const seen = feedbacks.filter(
    (feedback) => feedback.notificationClosedAt
  ).length;
  const percent = (count: number) => Math.round((count / total) * 100);

  const statuses = STATUS_ROWS.map((row) => ({
    ...row,
    count: feedbacks.filter((feedback) => feedback.status === row.status)
      .length,
  }));

  return (
    <div className="feedback-summary feedback-handling">
      <div className="feedback-average">
        <strong>{`${percent(answered)} %`}</strong>
      </div>
      <p className="feedback-average-label">
        {tr('handling.Answered', { count: answered, total })}
      </p>

      {/* The feedback by its status as one bar, then each status with its number */}
      <div className="feedback-handling-bar" aria-hidden="true">
        {statuses
          .filter((row) => row.count > 0)
          .map((row) => (
            <span
              className={`feedback-handling-part ${row.key}`}
              key={row.key}
              style={{ flexGrow: row.count }}
              title={`${tr(`statuses.${row.key}`)}: ${row.count}`}
            />
          ))}
      </div>
      {statuses.map((row) => (
        <p className="feedback-page-average" key={row.key}>
          <span>
            <i className={`feedback-handling-dot ${row.key}`} />
            {tr(`statuses.${row.key}`)}
          </span>
          <strong>{row.count}</strong>
          <small>{`${percent(row.count)} %`}</small>
        </p>
      ))}

      <p className="feedback-users">
        {tr('handling.Notified', { count: notified })}
        <br />
        {tr('handling.Seen', { count: seen })}
      </p>
    </div>
  );
};

export default FeedbackHandling;
